import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';
import { useDebounce } from 'use-debounce';
import KeywordItem from './KeywordItem';
import NGramDisplay from './NGramDisplay';
import FilterChipBar from './FilterChipBar';
import {
  importKeywords,
  setRawImportedKeywords,
  setKeywordVisibilityFilter,
  addFilterChip,
  removeFilterChip,
  setColumnFilter,
  restoreState,
  setSelectedKeywords,
  toggleKeywordSelection,
  rangeSelectKeywords,
  setNGramMatchMode,
  toggleSuperSelectMode,
  resetAppState
} from '../redux/actions';
import { KeywordVisibilityFilters } from '../redux/reducers/keywordReducer';
import store from '../redux/store';
import { getFolderPath, getMaxFolderPathDepth } from '../utils/folderUtils';
import { escapeRegExp } from '../utils/stringUtils';
import {
  selectDisplayedKeywords,
  selectSelectedKeywordIds,
  selectLastSelectedKeywordId,
  selectSuperSelectMode,
  selectColumnDefinitions,
  selectNGramMatchMode,
  selectImportedKeywordsRaw,
  selectGlobalRegexError,
  selectFilterChips,
  selectKeywordVisibilityFilter,
  selectVolumeFilterUI,
  selectKdFilterUI,
  selectCpcFilterUI
} from '../redux/keywordSelectors';

const ITEM_HEIGHT = 40;

const KeywordRow = React.memo(({ index, style, data }) => {
  const { items, selectedIds, clickHandler, colsDef, itemHeight } = data;
  const keyword = items[index];
  if (!keyword) return <div style={style}></div>;
  return (
    <div style={{ ...style, height: itemHeight, boxSizing: 'border-box', borderBottom: '1px solid #eee' }}>
      <KeywordItem id={keyword.id} text={keyword.originalText} isActive={keyword.isActive}
        data={keyword.data} onClick={clickHandler} isSelected={selectedIds.includes(keyword.id)}
        columnDefinitions={colsDef} />
    </div>
  );
});

const KeywordManagementPane = () => {
  const [inputText, setInputText] = useState('');
  const [ngramInput, setNgramInput] = useState({ type: 'none', data: null });
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const [localTextSearch, setLocalTextSearch] = useState('');
  const [localRegex, setLocalRegex] = useState('');
  const [localVolumeFilter, setLocalVolumeFilter] = useState({ operator: '', value1: '', value2: '' });
  const [localKdFilter, setLocalKdFilter] = useState({ operator: '', value1: '', value2: '' });
  const [localCpcFilter, setLocalCpcFilter] = useState({ operator: '', value1: '', value2: '' });

  const importedKeywordsRaw = useSelector(selectImportedKeywordsRaw);
  const visibilityFilter = useSelector(selectKeywordVisibilityFilter);
  const currentRegexError = useSelector(selectGlobalRegexError);
  const filterChips = useSelector(selectFilterChips);
  const nGramMatchMode = useSelector(selectNGramMatchMode);
  const superSelectMode = useSelector(selectSuperSelectMode);
  const selectedKeywordIds = useSelector(selectSelectedKeywordIds);
  const lastSelectedKeywordId = useSelector(selectLastSelectedKeywordId);
  const columnDefinitions = useSelector(selectColumnDefinitions);
  const displayedKeywords = useSelector(selectDisplayedKeywords);

  const volumeFilterUI = useSelector(selectVolumeFilterUI);
  const kdFilterUI = useSelector(selectKdFilterUI);
  const cpcFilterUI = useSelector(selectCpcFilterUI);

  useEffect(() => {
    console.log('volumeFilterUI:', volumeFilterUI);
    const textChip = filterChips.find(chip => chip.type === 'text');
    setLocalTextSearch(textChip ? textChip.params.searchTerm : '');
    const regexChip = filterChips.find(chip => chip.type === 'regex');
    setLocalRegex(regexChip ? regexChip.params.regexString : '');

    const normalizeFilter = (filter) => ({
      operator: filter?.operator || '',
      value1: typeof filter?.min === 'string' ? filter.min : typeof filter?.value1 === 'string' ? filter.value1 : '',
      value2: typeof filter?.max === 'string' ? filter.max : typeof filter?.value2 === 'string' ? filter.value2 : ''
    });

    const newVolumeFilter = normalizeFilter(volumeFilterUI);
    const newKdFilter = normalizeFilter(kdFilterUI);
    const newCpcFilter = normalizeFilter(cpcFilterUI);

    if (JSON.stringify(localVolumeFilter) !== JSON.stringify(newVolumeFilter)) setLocalVolumeFilter(newVolumeFilter);
    if (JSON.stringify(localKdFilter) !== JSON.stringify(newKdFilter)) setLocalKdFilter(newKdFilter);
    if (JSON.stringify(localCpcFilter) !== JSON.stringify(newCpcFilter)) setLocalCpcFilter(newCpcFilter);
  }, [filterChips, volumeFilterUI, kdFilterUI, cpcFilterUI]);

  useEffect(() => {
    if (selectedKeywordIds.length === 1) {
      let selectedKwObject = displayedKeywords.find(kw => kw.id === selectedKeywordIds[0]);
      if (!selectedKwObject) { const allKws = store.getState().keywords.allKeywords; selectedKwObject = allKws.find(kw => kw.id === selectedKeywordIds[0]); }
      setNgramInput({ type: 'single', data: selectedKwObject ? selectedKwObject.normalizedText : null });
    } else if (displayedKeywords.length > 0 && selectedKeywordIds.length !== 1) {
      const texts = displayedKeywords.map(kw => kw.normalizedText); setNgramInput({ type: 'multiple', data: texts });
    } else { setNgramInput({ type: 'none', data: null }); }
  }, [selectedKeywordIds, displayedKeywords]);

  useEffect(() => {
    if (filterChips.length > 0) {
      const lastChip = filterChips[filterChips.length - 1];
      if (lastChip.type === 'ngram' && displayedKeywords.length > 0) {
        const idsToSelect = displayedKeywords.map(kw => kw.id);
        if (JSON.stringify(selectedKeywordIds) !== JSON.stringify(idsToSelect)) dispatch(setSelectedKeywords(idsToSelect));
      }
    }
  }, [filterChips, displayedKeywords, dispatch, selectedKeywordIds]);

  useEffect(() => { if (importedKeywordsRaw && inputText === '') setInputText(importedKeywordsRaw); }, [importedKeywordsRaw, inputText]);

  // Debounced effects for column filters
  const debouncedVolumeMin = useDebounce(localVolumeFilter.value1, 500);
  const debouncedVolumeMax = useDebounce(localVolumeFilter.value2, 500);
  const debouncedKdMin = useDebounce(localKdFilter.value1, 500);
  const debouncedKdMax = useDebounce(localKdFilter.value2, 500);
  const debouncedCpcMin = useDebounce(localCpcFilter.value1, 500);
  const debouncedCpcMax = useDebounce(localCpcFilter.value2, 500);

  useEffect(() => {
    dispatch(setColumnFilter({ columnName: 'volume', min: debouncedVolumeMin, max: debouncedVolumeMax }));
  }, [debouncedVolumeMin, debouncedVolumeMax, dispatch]);
  useEffect(() => {
    dispatch(setColumnFilter({ columnName: 'kd', min: debouncedKdMin, max: debouncedKdMax }));
  }, [debouncedKdMin, debouncedKdMax, dispatch]);
  useEffect(() => {
    dispatch(setColumnFilter({ columnName: 'cpc', min: debouncedCpcMin, max: debouncedCpcMax }));
  }, [debouncedCpcMin, debouncedCpcMax, dispatch]);

  const createColumnChipManager = useCallback((columnId, headerName, debouncedMin, debouncedMax, currentOperator) => {
    const existingChip = filterChips.find(chip => chip.type === 'column' && chip.params.columnName === columnId);
    const minV = String(debouncedMin).trim();
    const maxV = String(debouncedMax).trim();

    if (minV !== '' || maxV !== '') {
      let label = `${headerName}: `;
      if (minV !== '' && maxV !== '') label += `${minV} - ${maxV}`;
      else if (minV !== '') label += `>= ${minV}`;
      else if (maxV !== '') label += `<= ${maxV}`;

      let operatorForChip = 'between';
      if (minV !== '' && maxV === '') operatorForChip = 'gte';
      else if (minV === '' && maxV !== '') operatorForChip = 'lte';

      if (!existingChip || existingChip.params.min !== minV || existingChip.params.max !== maxV || existingChip.params.operator !== operatorForChip) {
        if (existingChip) dispatch(removeFilterChip(existingChip.id));
        dispatch(addFilterChip({ type: 'column', label, params: { columnName: columnId, operator: operatorForChip, min: minV, max: maxV } }));
      }
    } else {
      if (existingChip) dispatch(removeFilterChip(existingChip.id));
    }
  }, [filterChips, dispatch]);

  useEffect(() => {
    const volColDef = columnDefinitions.find(cd => cd.headerName.toLowerCase() === 'volume');
    if (volColDef) createColumnChipManager(volColDef.id, 'Volume', debouncedVolumeMin, debouncedVolumeMax, localVolumeFilter.operator);
  }, [debouncedVolumeMin, debouncedVolumeMax, localVolumeFilter.operator, filterChips, dispatch, columnDefinitions, createColumnChipManager]);

  useEffect(() => {
    const kdColDef = columnDefinitions.find(cd => cd.headerName.toLowerCase() === 'kd');
    if (kdColDef) createColumnChipManager(kdColDef.id, 'KD', debouncedKdMin, debouncedKdMax, localKdFilter.operator);
  }, [debouncedKdMin, debouncedKdMax, localKdFilter.operator, filterChips, dispatch, columnDefinitions, createColumnChipManager]);

  useEffect(() => {
    const cpcColDef = columnDefinitions.find(cd => cd.headerName.toLowerCase() === 'cpc');
    if (cpcColDef) createColumnChipManager(cpcColDef.id, 'CPC', debouncedCpcMin, debouncedCpcMax, localCpcFilter.operator);
  }, [debouncedCpcMin, debouncedCpcMax, localCpcFilter.operator, filterChips, dispatch, columnDefinitions, createColumnChipManager]);

  const handleImportKeywords = () => {
    dispatch(setRawImportedKeywords(inputText));
    dispatch(importKeywords(inputText));
  };

  const handleItemClick = useCallback((keywordItem, event) => {
    event.preventDefault();
    const clickedKeywordId = keywordItem.id;
    const currentDisplayedIds = displayedKeywords.map(kw => kw.id);
    if (superSelectMode && (event.ctrlKey || event.metaKey) && event.shiftKey) {
      dispatch(setSelectedKeywords([])); return;
    }
    if (event.shiftKey && lastSelectedKeywordId) {
      dispatch(rangeSelectKeywords(clickedKeywordId, currentDisplayedIds));
    } else if (event.ctrlKey || event.metaKey) {
      dispatch(toggleKeywordSelection(clickedKeywordId));
    } else {
      if (superSelectMode) dispatch(toggleKeywordSelection(clickedKeywordId));
      else dispatch(setSelectedKeywords([clickedKeywordId]));
    }
  }, [dispatch, lastSelectedKeywordId, superSelectMode, displayedKeywords]);

  const handleVisibilityFilterChange = (filter) => dispatch(setKeywordVisibilityFilter(filter));

  const applyTextFilter = () => {
    const term = localTextSearch.trim();
    const existingChip = filterChips.find(chip => chip.type === 'text');
    if (existingChip) dispatch(removeFilterChip(existingChip.id));
    if (term) dispatch(addFilterChip({ type: 'text', label: `Text: "${term}"`, params: { searchTerm: term } }));
  };

  const applyRegexFilter = () => {
    const regexStr = localRegex.trim();
    const existingChip = filterChips.find(chip => chip.type === 'regex');
    if (existingChip) dispatch(removeFilterChip(existingChip.id));
    if (regexStr) dispatch(addFilterChip({ type: 'regex', label: `Regex: /${regexStr}/`, params: { regexString: regexStr } }));
  };

  const handleSaveState = () => {
    const currentFullState = store.getState();
    const stateToSave = { keywords: currentFullState.keywords, folders: currentFullState.folders, savedAt: new Date().toISOString() };
    const jsonString = JSON.stringify(stateToSave, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'keyword_tool_state.json'; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };
  const handleLoadState = (event) => {
    const file = event.target.files[0]; if (!file) return; const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const loadedState = JSON.parse(e.target.result);
        if (loadedState && loadedState.keywords && loadedState.folders) {
          dispatch(restoreState({ keywords: loadedState.keywords, folders: loadedState.folders }));
          setInputText(loadedState.keywords.importedKeywordsRaw || '');
          alert('State restored successfully!');
        } else { alert('Invalid state file format.'); }
      } catch (error) { console.error("Error loading state:", error); alert(`Error loading state: ${error.message}`);}
      finally { if(fileInputRef.current) fileInputRef.current.value = ""; }
    }; reader.readAsText(file);
  };
  const handleNGramClicked = (nGramText) => {
    if (!nGramText) return;
    const existingNgramChip = filterChips.find(chip => chip.type === 'ngram');
    if (existingNgramChip) dispatch(removeFilterChip(existingNgramChip.id));
    dispatch(addFilterChip({ type: 'ngram', label: `N-gram: "${nGramText}" (${nGramMatchMode})`, params: { nGramText, matchMode: nGramMatchMode } }));
  };
  const handleExportCategorizedCsv = () => {
    const { keywords: keywordsState, folders: foldersState } = store.getState();
    const allKws = keywordsState.allKeywords; const allFolders = foldersState.folders;
    if (allFolders.length === 0) { alert("No folders to export."); return; }
    const kwMap = new Map(allKws.map(kw => [kw.id, kw]));
    const colDefs = keywordsState.columnDefinitions || [];
    const dynamicHeaders = colDefs.sort((a,b) => a.displayOrder - b.displayOrder).map(col => col.headerName);
    let csvHeader = ["Keyword", ...dynamicHeaders];
    const maxDepth = getMaxFolderPathDepth(allFolders);
    for (let i = 1; i <= maxDepth; i++) csvHeader.push(`Category_L${i}`);
    const csvRows = [csvHeader.map(escapeRegExp).join(',')];
    allFolders.forEach(folder => {
      const folderPath = getFolderPath(folder.id, allFolders);
      folder.keywords.forEach(kwInFolder => {
        const kwObj = kwMap.get(kwInFolder.id);
        if (kwObj) {
          const row = [kwObj.originalText];
          colDefs.sort((a,b) => a.displayOrder - b.displayOrder).forEach(colDef => row.push(kwObj.data[colDef.id] ?? ''));
          for (let i = 0; i < maxDepth; i++) row.push(i < folderPath.length ? folderPath[i] : "");
          csvRows.push(row.map(escapeRegExp).join(','));
        }
      });
    });
    if (csvRows.length <= 1) { alert("No categorized keywords to export."); return; }
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'categorized_keywords.csv'; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const filterButtonStyle = (isActiveFilter) => ({ padding: '5px 10px', margin: '0 5px', border: isActiveFilter ? '2px solid #007bff' : '1px solid #ccc', backgroundColor: isActiveFilter ? '#e7f3ff' : 'white', cursor: 'pointer', borderRadius: '4px' });
  const inputStyle = { padding: '6px', margin: '5px', border: '1px solid #ccc', borderRadius: '4px', width: '120px' };
  const headerBaseStyle = { padding: '5px', fontWeight: 'bold', textAlign: 'left', borderBottom: '2px solid #ccc', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 };
  const headerKwStyle = { ...headerBaseStyle, flexGrow: 1, flexBasis: '200px', minWidth: '150px' };
  const headerDataStyle = { ...headerBaseStyle, flexGrow: 0, flexBasis: '100px', textAlign: 'right' };
  const headerActionsStyle = { ...headerBaseStyle, flexGrow: 0, flexBasis: '150px', textAlign: 'center' };
  const buttonStyle = { padding: '8px 12px', margin: '5px', cursor: 'pointer', border: '1px solid #007bff', backgroundColor: '#007bff', color: 'white', borderRadius: '4px' };
  const smallButtonStyle = { padding: '4px 8px', margin: '0 2px', fontSize: '0.85em', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '3px' };

  const renderMinMaxFilter = (colDef, localMin, setLocalMin, localMax, setLocalMax) => (
    <div style={{ display: 'flex', alignItems: 'center', marginRight: '15px', marginBottom: '5px' }}>
      <label style={{ marginRight: '5px', fontSize: '0.9em', whiteSpace: 'nowrap' }}>{colDef.headerName}:</label>
      <input type="number" placeholder="Min" value={localMin} onChange={e => setLocalMin(e.target.value)} style={{ ...inputStyle, width: '70px', height: '30px' }} />
      <input type="number" placeholder="Max" value={localMax} onChange={e => setLocalMax(e.target.value)} style={{ ...inputStyle, width: '70px', height: '30px', marginLeft: '5px' }} />
    </div>
  );

  const memoizedItemData = useMemo(() => ({
    items: displayedKeywords, selectedIds: selectedKeywordIds, clickHandler: handleItemClick,
    colsDef: columnDefinitions, itemHeight: ITEM_HEIGHT
  }), [displayedKeywords, selectedKeywordIds, handleItemClick, columnDefinitions]);

  return (
    <div style={{ border: '1px solid blue', padding: '10px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <h2>Keyword Management</h2>
        <div>
          <button onClick={handleSaveState} style={buttonStyle}>Save State</button>
          <input type="file" accept=".json" onChange={handleLoadState} style={{ display: 'none' }} ref={fileInputRef} />
          <button onClick={() => fileInputRef.current && fileInputRef.current.click()} style={buttonStyle}>Load State</button>
          <button onClick={handleExportCategorizedCsv} style={{ ...buttonStyle, backgroundColor: '#28a745' }}>Export CSV</button>
          <button onClick={() => dispatch(resetAppState())} style={{ ...buttonStyle, backgroundColor: '#dc3545', color: 'white', marginLeft: '10px' }}>Clear All Data</button>
        </div>
      </div>
      <textarea value={inputText} onChange={(e) => setInputText(e.target.value)} placeholder="Enter keywords, one per line (e.g., keyword,col1,col2...)" style={{ width: 'calc(100% - 12px)', minHeight: '60px', marginBottom: '10px', boxSizing: 'border-box', flexShrink: 0 }} />
      <button
        onClick={handleImportKeywords}
        style={{ ...buttonStyle, marginBottom: '10px', alignSelf: 'flex-start', flexShrink: 0 }}
        title="Paste keyword list (CSV/TSV format). This will replace all current keywords, columns, and folder categorizations."
      >
        Import & Replace All
      </button>

      <div style={{ flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '5px', flexWrap: 'wrap', gap: '5px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', alignItems: 'center' }}>
            <button onClick={() => handleVisibilityFilterChange(KeywordVisibilityFilters.SHOW_ACTIVE)} className={filterButtonStyle(visibilityFilter === KeywordVisibilityFilters.SHOW_ACTIVE)}>Active</button>
            <button onClick={() => handleVisibilityFilterChange(KeywordVisibilityFilters.SHOW_INACTIVE)} className={filterButtonStyle(visibilityFilter === KeywordVisibilityFilters.SHOW_INACTIVE)}>Inactive</button>
            <button onClick={() => handleVisibilityFilterChange(KeywordVisibilityFilters.SHOW_ALL_UNCATEGORIZED)} className={filterButtonStyle(visibilityFilter === KeywordVisibilityFilters.SHOW_ALL_UNCATEGORIZED)}>All Uncategorized</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <input type="text" placeholder="Text search..." value={localTextSearch} onChange={(e) => setLocalTextSearch(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && applyTextFilter()} style={{ ...inputStyle, width: '150px', height: '30px' }} />
              <button onClick={applyTextFilter} style={{ ...smallButtonStyle, height: '30px', marginLeft: '0px' }}>Apply</button>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input type="text" placeholder="Regex filter..." value={localRegex} onChange={(e) => setLocalRegex(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && applyRegexFilter()} style={{ ...inputStyle, width: '150px', height: '30px', borderColor: currentRegexError ? 'red' : '#ccc' }} />
                <button onClick={applyRegexFilter} style={{ ...smallButtonStyle, height: '30px', marginLeft: '0px' }}>Apply</button>
              </div>
              {currentRegexError && <p style={{ color: 'red', fontSize: '0.8em', margin: '2px 0 0 5px', width: '150px' }}>{currentRegexError}</p>}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '0px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
          {columnDefinitions.find(cd => cd.id === 'col_0') && renderMinMaxFilter(columnDefinitions.find(cd => cd.id === 'col_0'), localVolumeFilter.value1, (v) => setLocalVolumeFilter({ ...localVolumeFilter, value1: v }), localVolumeFilter.value2, (v) => setLocalVolumeFilter({ ...localVolumeFilter, value2: v }))}
          {columnDefinitions.find(cd => cd.id === 'col_1') && renderMinMaxFilter(columnDefinitions.find(cd => cd.id === 'col_1'), localKdFilter.value1, (v) => setLocalKdFilter({ ...localKdFilter, value1: v }), localKdFilter.value2, (v) => setLocalKdFilter({ ...localKdFilter, value2: v }))}
          {columnDefinitions.find(cd => cd.id === 'col_2') && renderMinMaxFilter(columnDefinitions.find(cd => cd.id === 'col_2'), localCpcFilter.value1, (v) => setLocalCpcFilter({ ...localCpcFilter, value1: v }), localCpcFilter.value2, (v) => setLocalCpcFilter({ ...localCpcFilter, value2: v }))}
        </div>
        <FilterChipBar />
      </div>

      <div style={{ display: 'flex', flexGrow: 1, overflow: 'hidden', minHeight: '200px' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: '400px' }}>
          <div style={{ display: 'flex', padding: '0 8px', borderBottom: '2px solid #ccc', backgroundColor: '#f0f0f0', fontSize: '0.9em', flexShrink: 0 }}>
            <div style={headerKwStyle} title="Keyword">KW</div>
            {(columnDefinitions || []).sort((a, b) => a.displayOrder - b.displayOrder).map(colDef => (
              <div key={colDef.id} style={headerDataStyle} title={colDef.headerName}>
                {colDef.headerName}
              </div>
            ))}
            <div style={headerActionsStyle}>Actions</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '5px 8px', flexShrink: 0, borderBottom: '1px solid #eee' }}>
            <button onClick={() => dispatch(setSelectedKeywords(displayedKeywords.map(kw => kw.id)))} style={smallButtonStyle} disabled={displayedKeywords.length === 0 || displayedKeywords.length === selectedKeywordIds.length}>Select All Displayed</button>
            <button onClick={() => dispatch(setSelectedKeywords([]))} style={smallButtonStyle} disabled={selectedKeywordIds.length === 0}>Deselect All</button>
            <button onClick={() => dispatch(toggleSuperSelectMode())} title={superSelectMode ? "Disable Super-select" : "Enable Super-select"} style={{ ...smallButtonStyle, marginLeft: '10px', backgroundColor: superSelectMode ? '#4CAF50' : '#f0f0f0', color: superSelectMode ? 'white' : 'black', minWidth: '120px' }}>Super-select: {superSelectMode ? 'ON' : 'OFF'}</button>
          </div>
          <div className="keyword-list-container" style={{ flexGrow: 1, height: '100%', width: '100%' }}>
            {displayedKeywords.length > 0 ? (
              <FixedSizeList height={600} width="100%" itemCount={displayedKeywords.length} itemSize={ITEM_HEIGHT} itemData={memoizedItemData}>
                {KeywordRow}
              </FixedSizeList>
            ) : ( <p style={{ textAlign: 'center', marginTop: '20px' }}>No keywords match current filters or none imported.</p> )}
          </div>
        </div>
        <div style={{ width: ngramInput.type !== 'none' ? '35%' : '0%', minWidth: ngramInput.type !== 'none' ? '300px' : '0px', maxWidth: ngramInput.type !== 'none' ? '500px' : '0px', overflowY: 'auto', paddingLeft: ngramInput.type !== 'none' ? '10px' : '0', borderLeft: ngramInput.type !== 'none' ? '1px solid #eee' : 'none', transition: 'width 0.3s ease, min-width 0.3s ease, max-width 0.3s ease', flexShrink: 0 }}>
          {ngramInput.type !== 'none' && (
            <>
              <NGramDisplay input={ngramInput} onNGramClicked={handleNGramClicked} />
              <div style={{ paddingTop: '10px', borderTop: '1px solid #ddd', marginTop: '10px' }}>
                <strong>N-gram Match Mode:</strong>
                <button onClick={() => dispatch(setNGramMatchMode('wholeWord'))} style={{ fontWeight: nGramMatchMode === 'wholeWord' ? 'bold' : 'normal', margin: '0 5px', padding: '3px 7px' }}>Whole Word</button>
                <button onClick={() => dispatch(setNGramMatchMode('substring'))} style={{ fontWeight: nGramMatchMode === 'substring' ? 'bold' : 'normal', padding: '3px 7px' }}>Substring</button>
              </div>
            </>
          )}
          {ngramInput.type === 'none' && <p style={{ color: '#777', textAlign: 'center', marginTop: '20px' }}>Select a single keyword or view all displayed for N-grams.</p>}
        </div>
      </div>
    </div>
  );
};

export default KeywordManagementPane;
